import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './auth';
import { Shell } from './components/Shell';
import { Alert, Spinner } from './components/ui';
import { SetupPage } from './pages/Setup';
import { LoginPage } from './pages/Login';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { AnalyticsPage } from './pages/Analytics';
import { BatchesPage } from './pages/Batches';
import { BatchDetailPage } from './pages/BatchDetail';
import { DestinationsPage } from './pages/Destinations';
import { UsersPage } from './pages/Users';
import { LlmSettingsPage } from './pages/LlmSettings';
import { BlobSettingsPage } from './pages/BlobSettings';
import { CosmosSettingsPage } from './pages/CosmosSettings';

function Boot({ children }: { children: ReactNode }) {
  const { needsSetup, user, bootError, refresh } = useAuth();
  const loc = useLocation();
  if (bootError) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1>Can't reach the server</h1>
          <Alert>{bootError}</Alert>
          <button className="btn btn-primary" onClick={() => void refresh()}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (needsSetup === undefined || user === undefined) return <Spinner />;
  if (needsSetup && loc.pathname !== '/setup') return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

function RequireAuth({ admin, children }: { admin?: boolean; children: ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (admin && user.role !== 'admin') {
    return (
      <div className="page">
        <Alert kind="warn">This page is only available to admins.</Alert>
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  return (
    <Boot>
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/batches" replace />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/batches" element={<BatchesPage />} />
          <Route path="/batches/:id" element={<BatchDetailPage />} />
          <Route path="/destinations" element={<DestinationsPage />} />
          <Route
            path="/users"
            element={
              <RequireAuth admin>
                <UsersPage />
              </RequireAuth>
            }
          />
          <Route path="/settings" element={<Navigate to="/settings/blob" replace />} />
          <Route
            path="/settings/blob"
            element={
              <RequireAuth admin>
                <BlobSettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings/cosmos"
            element={
              <RequireAuth admin>
                <CosmosSettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings/llm"
            element={
              <RequireAuth admin>
                <LlmSettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={
              <div className="page">
                <h1>Not found</h1>
                <p className="muted">There is nothing at this address.</p>
              </div>
            }
          />
        </Route>
      </Routes>
    </Boot>
  );
}
